import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const FIELDS = [
  ["google_business", "Google Business Profile", "https://g.page/r/..."],
  ["zillow", "Zillow", "https://www.zillow.com/profile/..."],
  ["realtor", "Realtor.com", "https://www.realtor.com/realestateagents/..."],
  ["facebook", "Facebook", "https://www.facebook.com/..."],
  ["instagram", "Instagram", "https://www.instagram.com/..."],
  ["linkedin", "LinkedIn", "https://www.linkedin.com/in/..."],
  ["youtube", "YouTube", "https://www.youtube.com/@..."],
  ["tiktok", "TikTok", "https://www.tiktok.com/@..."],
  ["x", "X", "https://x.com/..."],
];
const inp = "w-full px-3 py-2 bg-white border rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";

const validate = (links) => {
  const errs = {};
  const seen = new Map();
  const check = (key, v) => {
    const val = (v || "").trim();
    if (!val) return;
    let ok = false;
    try { const u = new URL(val); ok = u.protocol === "https:" && u.hostname.includes("."); } catch { ok = false; }
    if (!ok) { errs[key] = "Must be a full https:// link"; return; }
    const norm = val.replace(/\/+$/, "").toLowerCase();
    if (seen.has(norm)) errs[key] = "Duplicate link";
    else seen.set(norm, key);
  };
  FIELDS.forEach(([k]) => check(k, links[k]));
  (links.other || []).forEach((v, i) => check(`other-${i}`, v));
  return errs;
};

const toPayload = (links) => {
  const clean = Object.fromEntries(FIELDS.map(([k]) => [k, (links[k] || "").trim()]).filter(([, v]) => v));
  const other = (links.other || []).map((v) => v.trim()).filter(Boolean);
  return other.length ? { ...clean, other } : clean;
};

export const ProfileLinksCard = forwardRef(function ProfileLinksCard({ isAdmin, onSaved }, ref) {
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState(null);
  const [links, setLinks] = useState({});
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (uid) => {
    try {
      const { data: d } = await api.get("/aeo/profile-links", { params: uid ? { user_id: uid } : {} });
      setData(d);
      const l = { ...d.links, other: d.links?.other?.length ? d.links.other : [] };
      setLinks(l); setSaved(JSON.stringify(toPayload(l)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(userId); }, [load, userId]);

  const errs = validate(links);
  const dirty = !!data && JSON.stringify(toPayload(links)) !== saved;
  const save = async ({ quiet } = {}) => {
    if (Object.keys(errs).length) { toast.error("Fix the highlighted profile links first"); throw new Error("invalid links"); }
    setSaving(true);
    try {
      const { data: d } = await api.put("/aeo/profile-links", { user_id: data.user_id, links: toPayload(links) });
      const l = { ...d.links, other: d.links?.other || [] };
      setLinks(l); setSaved(JSON.stringify(toPayload(l)));
      onSaved?.(d.user_id, d.links);
      if (!quiet) toast.success("Profile links saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); throw err; }
    finally { setSaving(false); }
  };
  useImperativeHandle(ref, () => ({ saveIfDirty: () => (dirty ? save({ quiet: true }) : Promise.resolve()) }));

  if (!data) return null;
  const set = (k, v) => setLinks((l) => ({ ...l, [k]: v }));
  const setOther = (i, v) => setLinks((l) => ({ ...l, other: l.other.map((x, idx) => (idx === i ? v : x)) }));

  const Field = ({ k, label, ph, value, onChange, extra }) => (
    <div>
      {label && <span className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755]">{label}</span>}
      <div className="flex items-center gap-2 mt-1">
        <input data-testid={`profile-link-${k}`} className={`${inp} ${errs[k] ? "border-[#C44545]" : "border-[#E5E5E2]"}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={ph} />
        {extra}
      </div>
      {errs[k] && <p data-testid={`profile-link-error-${k}`} className="text-[11px] text-[#C44545] mt-1">{errs[k]}</p>}
    </div>
  );

  return (
    <div data-testid="profile-links-section" className="space-y-4">
      <p className="text-xs text-[#8a8a86]">Paste the exact link to your own profile page. These help Google and AI tools verify who you are. They are not shown on your page.</p>
      {isAdmin && data.agents?.length > 1 && (
        <select data-testid="profile-links-agent-select" className={`${inp} border-[#E5E5E2]`} value={data.user_id} onChange={(e) => setUserId(e.target.value)}>
          {data.agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.display_name || a.username}</option>)}
        </select>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, ph]) => Field({ k, label, ph, value: links[k], onChange: (v) => set(k, v) }))}
      </div>
      <div className="space-y-2">
        <span className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755]">Other profile link</span>
        {(links.other || []).map((v, i) => Field({ k: `other-${i}`, ph: "https://...", value: v, onChange: (x) => setOther(i, x),
          extra: <button type="button" data-testid={`profile-link-other-remove-${i}`} onClick={() => setLinks((l) => ({ ...l, other: l.other.filter((_, idx) => idx !== i) }))} className="p-2 rounded-sm text-[#992B2B] hover:bg-[#F6E4E4]"><Trash2 className="w-4 h-4" /></button> }))}
        <button type="button" data-testid="profile-link-other-add" onClick={() => setLinks((l) => ({ ...l, other: [...(l.other || []), ""] }))} className="inline-flex items-center gap-1 text-xs font-semibold text-[#F7941D] hover:underline"><Plus className="w-3.5 h-3.5" /> Add another link</button>
      </div>
      <div className="flex items-center gap-3">
        <button data-testid="profile-links-save" disabled={saving} onClick={() => save().catch(() => {})} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#162D6A] disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save profile links
        </button>
        {dirty && <span data-testid="profile-links-unsaved" className="text-xs text-[#9a5b12]">Unsaved changes. Save Profile also saves these.</span>}
      </div>
    </div>
  );
});
